import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Card,
  Center,
  Container,
  Grid,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  Title,
  ThemeIcon,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconBuilding,
  IconMapPin,
  IconDeviceFloppy,
  IconPlus,
} from '@tabler/icons-react';
import { notify } from '@/lib/notify';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMedplum } from '@medplum/react-hooks';
import { usePermissions } from '../../../contexts/PermissionContext';
import { swapPracticeOwner } from '../../../lib/adminActionsService';
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog';
import {
  Practice,
  PracticeLocation,
  User,
} from '../../users/types/rbac';
import { PracticeProfileContent } from './PracticeProfileContent';
import { LocationTileMap } from './LocationTileMap';
import { EquipmentManagementSection } from './EquipmentManagementSection';
import { RoomManagementSection } from './RoomManagementSection';
import { AddressSearchInput } from './AddressSearchInput';
import { useLocationResources } from '../../../hooks/useLocationResources';
import {
  getDefaultSiteWeeklyHours,
  locationHasWeeklyHours,
  type PracticeRoomLocationInput,
  type PracticeSiteLocationInput,
} from '@unify/fhir-model/resources/location';
import { practiceSiteSchema } from '@/schemas';
import { WeeklyAvailabilityEditor } from '@/features/calendar/WeeklyAvailabilityEditor';
import { getProviderWeeklyAvailabilityUiDisableReason } from '@/features/calendar/providerAvailabilityValidation';
import {
  formatWeeklyHoursSummary,
  uiToWeeklyHours,
  weeklyHoursToUi,
} from '@/lib/locationWeeklyHoursUi';
import { US_STATES as US_STATE_OPTIONS } from '../../onboarding/constants';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

interface PracticeSettingsProps {
  practice: Practice;
  /** Directory users from Medplum (same source as Practices & Users). Filtered by this practice. */
  directoryUsers: User[];
  onBack: () => void;
  onUpdatePractice: (id: string, updates: Partial<Practice>) => Promise<boolean> | void;
  onDeletePractice?: (id: string) => void;
  /** When true (e.g. from Users), show the practice name as the page title instead of "Practice Settings" */
  usePracticeNameAsTitle?: boolean;
  /** When true, hide the back arrow (e.g. when scoped to current user's practice from Settings). */
  hideBackButton?: boolean;
  /** Initial tab to display (e.g. from ?tab= query param). */
  initialTab?: string;
  /** Directory users for this practice (Medplum admin directory); omit or pass [] when none. */
  practiceUsers?: User[];
}

const PRACTICE_SETTINGS_TABS = new Set(['profile', 'locations']);

export function PracticeSettings({
  practice,
  directoryUsers: _directoryUsers,
  onBack,
  onUpdatePractice,
  usePracticeNameAsTitle = false,
  hideBackButton = false,
  initialTab,
  practiceUsers = [],
}: PracticeSettingsProps) {
  const medplum = useMedplum();
  const { role: sessionRole, permissions } = usePermissions();
  const canEditSettings = permissions?.can_edit_settings !== false;
  const [, setSearchParams] = useSearchParams();
  const locationResources = useLocationResources({
    organizationId: practice.id,
    organizationDisplay: practice.name,
  });
  const [editedPractice, setEditedPractice] = useState(practice);
  const [hasChanges, setHasChanges] = useState(false);
  const [profileDirty, setProfileDirty] = useState(false);
  const [activeTab, _setActiveTab] = useState<string | null>(
    initialTab && PRACTICE_SETTINGS_TABS.has(initialTab) ? initialTab : 'profile',
  );
  const [incomingOwnerId, setIncomingOwnerId] = useState<string | null>(null);
  const [ownershipSwapLoading, setOwnershipSwapLoading] = useState(false);
  const canTransferOwnership =
    sessionRole === 'unify-admin' && permissions?.edit_users === true;

  const ownershipCandidates = useMemo(
    () =>
      practiceUsers
        .filter(
          (u) =>
            u.id !== practice.ownerId
            && u.role !== 'client'
            && u.status !== 'Inactive',
        )
        .map((u) => ({
          value: u.id,
          label: `${u.name || u.email || u.id} (${u.role})`,
        })),
    [practiceUsers, practice.ownerId],
  );

  const setActiveTab = (tab: string | null) => {
    _setActiveTab(tab);
    if (tab) {
      setSearchParams({ tab }, { replace: true });
    }
  };

  const [showAddLocation, setShowAddLocation] = useState(false);
  const [editingLocationId, setEditingLocationId] = useState<string | null>(null);
  const [locationFormError, setLocationFormError] = useState<string | null>(null);
  const [newLocation, setNewLocation] = useState<Partial<PracticeLocation>>({
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    hours: getDefaultSiteWeeklyHours(),
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (hasChanges) return;
    setEditedPractice(practice);
  }, [practice, hasChanges]);

  // Blur when location modal opens so map tiles/cards don't stay highlighted
  useEffect(() => {
    if (showAddLocation || editingLocationId) {
      (document.activeElement as HTMLElement | null)?.blur();
    }
  }, [showAddLocation, editingLocationId]);

  // Deletion dialog states
  const [showDeleteLocationDialog, setShowDeleteLocationDialog] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<string | null>(null);
  const [locationSaving, setLocationSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates: Partial<Practice> = {};

      if (profileDirty) {
        updates.name = editedPractice.name;
        updates.status = editedPractice.status;
        updates.email = editedPractice.email;
        updates.phone = editedPractice.phone;
        updates.websiteUrl = editedPractice.websiteUrl;
        updates.legalName = editedPractice.legalName;
        updates.taxId = editedPractice.taxId;
        updates.npi = editedPractice.npi;
        updates.legalEntityAddress = editedPractice.legalEntityAddress;
      }

      await onUpdatePractice(practice.id, updates);
      setHasChanges(false);
      setProfileDirty(false);
      notify({ title: 'Settings saved', message: 'Practice settings updated successfully.', type: 'success' });
    } catch (err) {
      const msg = getUserFacingErrorMessage(err);
      notify({ title: 'Could not save settings', message: msg, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAddLocation = () => {
    (document.activeElement as HTMLElement | null)?.blur();
    setLocationFormError(null);
    setEditingLocationId(null);
    setNewLocation({
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      hours: getDefaultSiteWeeklyHours(),
    });
    setShowAddLocation(true);
  };

  const handleOpenEditLocation = (location: PracticeLocation) => {
    (document.activeElement as HTMLElement | null)?.blur();
    setLocationFormError(null);
    setShowAddLocation(false);
    setEditingLocationId(location.id);
    setNewLocation({
      name: location.name,
      addressLine1: location.addressLine1,
      addressLine2: location.addressLine2 ?? '',
      city: location.city,
      state: location.state,
      zipCode: location.zipCode,
      phone: location.phone ?? '',
      latitude: location.latitude,
      longitude: location.longitude,
      status: location.status ?? 'active',
      hours: locationHasWeeklyHours(location.hours) ? location.hours : getDefaultSiteWeeklyHours(),
    });
  };

  const handleSaveLocation = async () => {
    setLocationFormError(null);
    const result = practiceSiteSchema.safeParse({
      name: newLocation.name,
      addressLine1: newLocation.addressLine1,
      addressLine2: newLocation.addressLine2 || undefined,
      city: newLocation.city,
      state: newLocation.state,
      zipCode: newLocation.zipCode,
      phone: newLocation.phone || undefined,
      latitude: newLocation.latitude,
      longitude: newLocation.longitude,
      status: newLocation.status ?? 'active',
    });
    if (!result.success) {
      const messages = result.error.issues.map((i) => i.message);
      setLocationFormError(`Please fix: ${messages.join(', ')}`);
      return;
    }
    const hours = newLocation.hours ?? {};
    if (!locationHasWeeklyHours(hours)) {
      setLocationFormError('Select at least one open day with start and end times');
      return;
    }
    const hoursReason = getProviderWeeklyAvailabilityUiDisableReason(weeklyHoursToUi(hours));
    if (hoursReason) {
      setLocationFormError(hoursReason);
      return;
    }
    setLocationSaving(true);
    const validated = result.data;
    const payload: PracticeSiteLocationInput = {
      name: validated.name,
      addressLine1: validated.addressLine1,
      addressLine2: validated.addressLine2 || undefined,
      city: validated.city,
      state: validated.state,
      zipCode: validated.zipCode,
      phone: validated.phone || undefined,
      latitude: validated.latitude,
      longitude: validated.longitude,
      status: validated.status as PracticeSiteLocationInput['status'],
      hours,
    };
    try {
      if (editingLocationId) {
        await locationResources.updateSite(editingLocationId, payload);
        setEditingLocationId(null);
      } else {
        await locationResources.createSite(payload);
        setShowAddLocation(false);
      }
      setNewLocation({
        name: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: '',
        phone: '',
      });
    } catch (err) {
      const msg = getUserFacingErrorMessage(err, 'Please check the details and try again.');
      setLocationFormError(msg);
      notify({ title: 'Could not save location', message: msg, type: 'error' });
    } finally {
      setLocationSaving(false);
    }
  };

  const handleCloseLocationForm = () => {
    setLocationFormError(null);
    setShowAddLocation(false);
    setEditingLocationId(null);
    setNewLocation({
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      phone: '',
      hours: getDefaultSiteWeeklyHours(),
    });
  };

  const handleDeleteLocation = (id: string) => {
    setLocationToDelete(id);
    setShowDeleteLocationDialog(true);
  };

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="lg">
            {!hideBackButton && (
              <ActionIcon variant="subtle" onClick={onBack} size="lg">
                <IconArrowLeft size={24} />
              </ActionIcon>
            )}
            <div>
              <Title order={2}>
                {usePracticeNameAsTitle ? practice.name : 'Practice Settings'}
              </Title>
              <Text c="gray.6" size="sm">
                Manage practice information and settings
              </Text>
            </div>
          </Group>

          {canEditSettings && activeTab !== 'locations' && (
            <Group gap="sm">
              <Button
                leftSection={<IconDeviceFloppy size={18} />}
                onClick={() => void handleSave()}
                disabled={!hasChanges || saving}
                loading={saving}
              >
                Save Changes
              </Button>
            </Group>
          )}
        </Group>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="profile" leftSection={<IconBuilding size={16} />}>
              Profile
            </Tabs.Tab>
            <Tabs.Tab value="locations" leftSection={<IconMapPin size={16} />}>
              Locations
            </Tabs.Tab>
          </Tabs.List>

          {/* Profile Tab – identity, branding, demographics */}
          <Tabs.Panel value="profile" pt="lg">
            <Stack gap="lg">
              <PracticeProfileContent
                practice={editedPractice}
                canEdit={canEditSettings}
                onProfileChange={(updates) => {
                  setEditedPractice((prev) => ({ ...prev, ...updates }));
                  setProfileDirty(true);
                  setHasChanges(true);
                }}
              />
              <Card withBorder>
                <Stack gap="md">
                  <Title order={4}>Practice ownership</Title>
                  <Text size="sm" c="dimmed">
                    Current owner: <strong>{practice.ownerName || practice.ownerId || '—'}</strong>
                  </Text>
                  {canTransferOwnership ? (
                    <>
                      <Text size="sm">
                        Transfer ownership in one bundled swap (exactly one practice-owner at a
                        time). The outgoing owner becomes a practice admin.
                      </Text>
                      <Select
                        label="Incoming practice owner"
                        placeholder="Select a practice member"
                        data={ownershipCandidates}
                        value={incomingOwnerId}
                        onChange={setIncomingOwnerId}
                        searchable
                        clearable
                        style={{ maxWidth: 420 }}
                      />
                      <Group>
                        <Button
                          loading={ownershipSwapLoading}
                          disabled={!incomingOwnerId}
                          onClick={async () => {
                            if (!incomingOwnerId) return;
                            setOwnershipSwapLoading(true);
                            try {
                              await swapPracticeOwner(medplum, {
                                orgId: practice.id,
                                outgoingOwnerId: practice.ownerId,
                                incomingOwnerId,
                                outgoingNewRole: 'practice-admin',
                              });
                              const incoming = practiceUsers.find((u) => u.id === incomingOwnerId);
                              const ownerPatch = {
                                ownerId: incomingOwnerId,
                                ownerName: incoming?.name || incoming?.email || incomingOwnerId,
                              };
                              setEditedPractice((prev) => ({
                                ...prev,
                                ...ownerPatch,
                              }));
                              await onUpdatePractice(practice.id, ownerPatch);
                              setIncomingOwnerId(null);
                              notify({
                                title: 'Practice owner swapped',
                                message: 'Ownership transferred successfully.',
                                type: 'success',
                              });
                            } catch (err) {
                              notify({
                                title: 'Ownership swap failed',
                                error: err,
                                type: 'error',
                              });
                            } finally {
                              setOwnershipSwapLoading(false);
                            }
                          }}
                        >
                          Transfer ownership
                        </Button>
                      </Group>
                    </>
                  ) : (
                    <Text size="sm" c="dimmed">
                      Only a Unify Admin can transfer practice ownership.
                    </Text>
                  )}
                </Stack>
              </Card>
            </Stack>
          </Tabs.Panel>

          {/* Locations Tab */}
          <Tabs.Panel value="locations" pt="lg">
            <Card withBorder>
              <Box mb="md" style={{ position: 'relative', zIndex: 10 }}>
                <Group justify="space-between">
                  <div>
                    <Title order={4}>Practice Locations</Title>
                    <Text size="sm" c="gray.6" mt="xs">
                      Manage physical office locations for in-person sessions
                    </Text>
                  </div>
                  {canEditSettings && (
                    <Button
                      type="button"
                      leftSection={<IconPlus size={16} />}
                      onClick={handleOpenAddLocation}
                      aria-label="Add new location"
                    >
                      Add Location
                    </Button>
                  )}
                </Group>
              </Box>

              {locationResources.error && (
                <Alert color="red" title="Could not load locations" mb="md">
                  {locationResources.error}
                </Alert>
              )}

              {/* Add / Edit Location Modal */}
              <Modal
                opened={!!(showAddLocation || editingLocationId)}
                onClose={handleCloseLocationForm}
                title={editingLocationId ? 'Edit Location' : 'Add New Location'}
                size="xl"
              >
                <Stack gap="lg">
                  <TextInput
                    label="Location Name"
                    withAsterisk
                    placeholder="e.g., Downtown Office"
                    value={newLocation.name}
                    onChange={(e) =>
                      setNewLocation({ ...newLocation, name: e.target.value })
                    }
                  />

                  <AddressSearchInput
                    label="Street Address Line 1"
                    withAsterisk
                    placeholder="e.g., 123 Main St or search by address"
                    value={newLocation.addressLine1 ?? ''}
                    onChange={(v) => {
                      // Autocomplete can set value to full label "Street — City, State Zip"; keep only street part
                      const streetOnly = v.includes(' — ') ? v.split(' — ')[0].trim() : v;
                      setNewLocation((prev) => ({ ...prev, addressLine1: streetOnly }));
                    }}
                    onSelect={(suggestion) =>
                      setNewLocation((prev) => ({
                        ...prev,
                        addressLine1: suggestion.address,
                        city: suggestion.city,
                        state: suggestion.state,
                        zipCode: suggestion.zipCode,
                        latitude: suggestion.latitude,
                        longitude: suggestion.longitude,
                      }))
                    }
                    hideDescription
                  />

                  <TextInput
                    label="Street Address Line 2 (optional)"
                    placeholder="e.g., Suite 200"
                    value={newLocation.addressLine2 ?? ''}
                    onChange={(e) =>
                      setNewLocation({ ...newLocation, addressLine2: e.target.value })
                    }
                  />

                  <Grid>
                    <Grid.Col span={{ base: 12, sm: 4 }}>
                      <TextInput
                        label="City"
                        withAsterisk
                        placeholder="e.g., San Francisco"
                        value={newLocation.city}
                        onChange={(e) =>
                          setNewLocation({ ...newLocation, city: e.target.value })
                        }
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, sm: 4 }}>
                      <Select
                        label="State"
                        withAsterisk
                        placeholder="Select state"
                        searchable
                        data={US_STATE_OPTIONS}
                        value={newLocation.state || null}
                        onChange={(v) =>
                          setNewLocation({ ...newLocation, state: v ?? '' })
                        }
                      />
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, sm: 4 }}>
                      <TextInput
                        label="Zip Code"
                        withAsterisk
                        placeholder="e.g., 94102"
                        value={newLocation.zipCode}
                        onChange={(e) =>
                          setNewLocation({ ...newLocation, zipCode: e.target.value })
                        }
                      />
                    </Grid.Col>
                  </Grid>

                  <TextInput
                    label="Phone Number (Optional)"
                    type="tel"
                    placeholder="e.g., (415) 555-0100"
                    value={newLocation.phone}
                    onChange={(e) =>
                      setNewLocation({ ...newLocation, phone: e.target.value })
                    }
                  />

                  <Select
                    label="Status"
                    data={[
                      { value: 'active', label: 'Active' },
                      { value: 'inactive', label: 'Inactive' },
                    ]}
                    value={newLocation.status ?? 'active'}
                    onChange={(v) =>
                      setNewLocation({ ...newLocation, status: (v as 'active' | 'inactive') ?? 'active' })
                    }
                  />

                  <Stack gap="xs">
                    <Text size="sm" fw={500}>
                      Hours
                    </Text>
                    <Text size="xs" c="gray.6">
                      Hours are in Mountain Time. Rooms inherit these hours unless they have custom hours.
                    </Text>
                    <WeeklyAvailabilityEditor
                      value={weeklyHoursToUi(newLocation.hours ?? getDefaultSiteWeeklyHours())}
                      onChange={(weekly) =>
                        setNewLocation({ ...newLocation, hours: uiToWeeklyHours(weekly) })
                      }
                    />
                  </Stack>

                  {locationFormError !== null && (
                    <Alert
                      color="red"
                      title={
                        locationFormError.startsWith('Please fill in:')
                          ? 'Missing required fields'
                          : 'Could not save location'
                      }
                      withCloseButton
                      onClose={() => setLocationFormError(null)}
                    >
                      {locationFormError}
                    </Alert>
                  )}

                  <Group justify={editingLocationId ? 'space-between' : 'flex-end'} mt="md">
                    {editingLocationId && (
                      <Button
                        variant="subtle"
                        color="red"
                        onClick={() => {
                          handleCloseLocationForm();
                          handleDeleteLocation(editingLocationId);
                        }}
                      >
                        Delete
                      </Button>
                    )}
                    <Group gap="sm">
                      <Button variant="outline" onClick={handleCloseLocationForm}>
                        Cancel
                      </Button>
                      <Button loading={locationSaving} onClick={() => void handleSaveLocation()}>
                        {editingLocationId ? 'Save Changes' : 'Add Location'}
                      </Button>
                    </Group>
                  </Group>
                </Stack>
              </Modal>

              {/* Locations List */}
              {locationResources.loading && locationResources.sites.length === 0 ? (
                <Center py="xl">
                  <Loader size="lg" />
                </Center>
              ) : locationResources.sites.length === 0 ? (
                <Group justify="center" py="xl">
                  <Stack align="center" gap="lg">
                    <ThemeIcon size={64} radius="md" variant="light" color="gray">
                      <IconMapPin size={32} />
                    </ThemeIcon>
                    <div>
                      <Text c="gray.6" size="sm" ta="center">
                        No locations added yet
                      </Text>
                      <Text c="gray.6" size="xs" ta="center" mt="xs">
                        {`Click "Add Location" to get started`}
                      </Text>
                    </div>
                  </Stack>
                </Group>
              ) : (
                <Stack>
                  {locationResources.sites.map((location) => (
                    <Card key={location.id} withBorder p="md">
                      <Group justify="space-between">
                        <Group gap="md">
                          <LocationTileMap location={location} size={160} />
                          <div>
                            <Text fw={500} size="sm">
                              {location.name}
                            </Text>
                            <Text size="xs" c="gray.6" mt="xs">
                              {location.addressLine1 ?? (location as { address?: string }).address ?? ''}
                              {location.addressLine2 ? `, ${location.addressLine2}` : ''}
                            </Text>
                            <Text size="xs" c="gray.6">
                              {location.city}, {location.state} {location.zipCode}
                            </Text>
                            {location.phone && (
                              <Text size="xs" c="gray.6" mt="xs">
                                {location.phone}
                              </Text>
                            )}
                            {formatWeeklyHoursSummary(location.hours ?? {}) ? (
                              <Text size="xs" c="gray.6" mt="xs">
                                {formatWeeklyHoursSummary(location.hours ?? {})}
                              </Text>
                            ) : (
                              <Text size="xs" c="red.7" mt="xs">
                                Set hours to enable room booking.
                              </Text>
                            )}
                          </div>
                        </Group>
                        {canEditSettings && (
                          <Button
                            size="xs"
                            variant="light"
                            onClick={() => handleOpenEditLocation(location)}
                            aria-label={`Edit ${location.name}`}
                          >
                            Edit
                          </Button>
                        )}
                      </Group>
                      <RoomManagementSection
                        rooms={locationResources.roomsBySiteId[location.id] ?? []}
                        siteHours={location.hours ?? {}}
                        fixedEquipmentByRoom={
                          (locationResources.equipmentBySiteId[location.id] ?? [])
                            .filter((e) => e.portability === 'fixed' && e.homeRoomId)
                            .reduce<{ roomId: string; equipmentNames: string[] }[]>((acc, e) => {
                              const existing = acc.find((x) => x.roomId === e.homeRoomId);
                              if (existing) existing.equipmentNames.push(e.name);
                              else acc.push({ roomId: e.homeRoomId!, equipmentNames: [e.name] });
                              return acc;
                            }, [])
                        }
                        canEdit={canEditSettings}
                        onSaveRoom={async (editingRoomId, data) => {
                          const input: PracticeRoomLocationInput = {
                            name: data.name,
                            roomType: data.type,
                            capacity: data.capacity,
                            adaAccessible: data.adaAccessible,
                            status: data.status,
                            hours: data.hours,
                          };
                          if (editingRoomId) {
                            await locationResources.updateRoom(location.id, editingRoomId, input);
                          } else {
                            await locationResources.createRoom(location.id, input);
                          }
                        }}
                        onDeleteRoom={(roomId) => locationResources.deleteRoom(roomId)}
                        onToggleRoomStatus={async (roomId, active) => {
                          const rooms = locationResources.roomsBySiteId[location.id] ?? [];
                          const r = rooms.find((x) => x.id === roomId);
                          if (!r) return;
                          await locationResources.updateRoom(location.id, roomId, {
                            name: r.name,
                            roomType: r.type,
                            capacity: r.capacity,
                            adaAccessible: r.adaAccessible,
                            status: active ? 'active' : 'inactive',
                            hours: r.hours,
                          });
                        }}
                      />
                      <EquipmentManagementSection
                        therapyRooms={locationResources.roomsBySiteId[location.id] ?? []}
                        equipment={locationResources.equipmentBySiteId[location.id] ?? []}
                        canEdit={canEditSettings}
                        onSave={async (editingEqId, data) => {
                          if (editingEqId) {
                            await locationResources.updateEquipment(location.id, editingEqId, {
                              name: data.name,
                              portability: data.portability,
                              homeRoomId: data.homeRoomId,
                              status: data.status,
                            });
                          } else {
                            await locationResources.createEquipment(location.id, {
                              name: data.name,
                              portability: data.portability,
                              homeRoomId: data.homeRoomId,
                              status: data.status,
                            });
                          }
                        }}
                        onDelete={(equipmentId) => locationResources.deleteEquipment(equipmentId)}
                        onToggleStatus={async (equipmentId, active) => {
                          const items = locationResources.equipmentBySiteId[location.id] ?? [];
                          const eq = items.find((x) => x.id === equipmentId);
                          if (!eq) return;
                          await locationResources.updateEquipment(location.id, equipmentId, {
                            name: eq.name,
                            portability: eq.portability,
                            homeRoomId: eq.homeRoomId,
                            status: active ? 'active' : 'inactive',
                          });
                        }}
                      />
                    </Card>
                  ))}
                </Stack>
              )}
            </Card>
          </Tabs.Panel>
        </Tabs>

        {/* Delete Dialogs */}
        <DeleteConfirmationDialog
          isOpen={showDeleteLocationDialog}
          onClose={() => {
            setShowDeleteLocationDialog(false);
            setLocationToDelete(null);
          }}
          onConfirm={() => {
            void (async () => {
              if (locationToDelete) {
                try {
                  await locationResources.deleteSite(locationToDelete);
                } catch (err) {
                  notify({
                    title: 'Could not delete location',
                    message: 'Could not delete this location.',
                    error: err,
                    type: 'error',
                  });
                }
              }
              setShowDeleteLocationDialog(false);
              setLocationToDelete(null);
            })();
          }}
          title="Delete Location"
          description="Are you sure you want to delete this location?"
          confirmText="Delete Location"
        />
      </Stack>
    </Container>
  );
}

